import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fromJS, is as isCompareImmutable } from "immutable";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import SecondToolbarAction from "../../components/SecondToolbarAction";
import { formatCompactAmount } from "../../api/DataTypeUtils";
import Pagination from "../../grid/Pagination";
import FilterAction from "../../actions/FilterAction";
import ActivityAction from "../../actions/ActivityAction";
import ViewMode from "./ViewMode";
import ViewSettings from "./view/ViewSettings";
import SortBy from "./sort/SortBy";
import AdvancedFilter from "./filter/AdvancedFilter";
import { SquareSigma, Plus, Mail, ChevronDown, Grid2X2Plus } from "lucide-react";
import { ContentTypes, ActivityTypes } from "../../constants/action-types";
import TaskAction from "../../actions/TaskAction";
import ViewModeDialog from "../../dialog/ViewModeDialog";
import LayoutAction from "../../actions/LayoutAction";

const SearchFilter = ({
  onChangeFilterAndSort,
  onExportCSV,
  contentType,
  pagination,
  projectId,
  currency,
  items,
  appView,
  projectViewId,
  projectView,
  goToPage,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const myFilterLoading = useSelector((state: any) => state.filterReducer.get("myFilterLoading"));
  const myFilters = useSelector((state: any) => state.filterReducer.get("myFilters")) || fromJS([]);
  const myFiltersByType = myFilters.filter((item) =>
    (item.getIn(["data", "types"]).toJS() || []).includes(contentType)
  );
  const tenantInfo = useSelector((state: any) => state.tenant.get("tenantInfo"));
  const isJira = tenantInfo?.getIn?.(["host", "product"]) === "JIRA";
  const fields =
    useSelector((state: any) => state.projectReducer.getIn(["project", "fields"])) || fromJS([]);
  const settingColumns =
    useSelector((state: any) => state.grid.getIn(["viewSetting", contentType, "columns"])) ||
    fromJS([]);
  const defaultColumns = useMemo(() => {
    const fromViewSetting = settingColumns.filter((item) => item.get("show"));
    if (fromViewSetting.size > 0) {
      return isJira
        ? fromViewSetting
        : fromViewSetting.filter((item) => item.get("selector") !== "issue");
    }
    return fields
      .filter((f) => f.get("formType") === contentType && (isJira || f.get("id") !== "issue"))
      .map((f) => ({
        name: f.get("label"),
        selector: f.get("id"),
        width: f.get("id") === "name" ? 350 : 150,
        show: true,
        isCustomField: f.get("isCustomField"),
        formType: f.get("formType"),
        key: f.get("id"),
        id: f.get("id"),
      }));
  }, [settingColumns, contentType, isJira, fields]);

  const filters = useSelector((state: any) => state.taskReducer.get("filters")) || fromJS({});
  const sorts = useSelector((state: any) => state.taskReducer.get("sorts")) || fromJS([]);
  const groupBy = useSelector((state: any) => state.taskReducer.get("groupBy"));
  const groupByVisibleFieldIds =
    useSelector((state: any) => state.taskReducer.get("groupByVisibleFieldIds")) || fromJS([]);
  const boardCardFields =
    useSelector((state: any) => state.taskReducer.get("boardCardFields")) || fromJS([]);
  const gridFields =
    useSelector((state: any) => state.taskReducer.get("gridFields")) || defaultColumns;
  const showCardLabels = useSelector((state: any) => state.taskReducer.get("showCardLabels"));

  const isHideViewMode =
    contentType === ContentTypes.ACTIVITY &&
    (appView === "Tasks" ||
      appView === "Notes" ||
      appView === "Drafts" ||
      appView === "Outbox" ||
      appView === "Tracked");

  useEffect(() => {
    if (!myFilterLoading && myFiltersByType.size > 0) {
      let myFilterData = myFiltersByType.find(
        (item) => !item.get("id") && item.getIn(["data", "view"]) === projectView
      );
      if (projectViewId) {
        myFilterData = myFiltersByType.find((item) => item.get("id") === projectViewId);
      }
      if (myFilterData && myFilterData.get("id")) {
        onChangeFilterAndSort(
          myFilterData.getIn(["data", "filters"])?.toJS() || {},
          myFilterData.getIn(["data", "sorts"])?.toJS() || []
        );
        const groupByData = myFilterData.getIn(["data", "groupBy"]) || "bucketId";
        const groupByVisibleFieldIdsData =
          myFilterData.getIn(["data", "groupByVisibleFieldIds"]) || fromJS([]);
        dispatch(TaskAction.changeGroupBy(groupByData, groupByVisibleFieldIdsData.toJS()));
        const boardCardFieldsData = myFilterData.getIn(["data", "boardCardFields"]) || fromJS([]);
        dispatch(TaskAction.changeBoardCardFields(boardCardFieldsData.toJS()));
        const gridFieldsData = myFilterData.getIn(["data", "gridFields"]) || defaultColumns;
        dispatch(TaskAction.changeGridFields(gridFieldsData.toJS()));
        const showCardLabelsData = myFilterData.getIn(["data", "showCardLabels"]);
        dispatch(TaskAction.changeShowCardLabels(showCardLabelsData));
      } else {
        onChangeFilterAndSort({}, []);
        dispatch(TaskAction.changeGroupBy("bucketId", [""]));
        dispatch(TaskAction.changeBoardCardFields([]));
        dispatch(TaskAction.changeGridFields(defaultColumns.toJS()));
        dispatch(TaskAction.changeShowCardLabels(false));
      }
    }
  }, [projectViewId, projectView, appView, myFilterLoading, myFiltersByType.size, defaultColumns]); // eslint-disable-line react-hooks/exhaustive-deps

  const isMyFilterChange = useMemo(() => {
    const currentData = fromJS({
      filters: filters.toJS(),
      sorts: sorts.toJS(),
      groupBy,
      groupByVisibleFieldIds: groupByVisibleFieldIds.toJS(),
      boardCardFields: boardCardFields.toJS(),
      gridFields: gridFields.toJS(),
      showCardLabels,
    });
    let compareData = fromJS({
      filters: {},
      sorts: [],
      groupBy: "bucketId",
      groupByVisibleFieldIds: [""],
      boardCardFields: [],
      gridFields: defaultColumns.toJS(),
      showCardLabels: false,
    });
    if (projectViewId) {
      const myFilterData = myFiltersByType.find((item) => item.get("id") === projectViewId);
      if (myFilterData && myFilterData.get("id")) {
        compareData = fromJS({
          filters: myFilterData.getIn(["data", "filters"])?.toJS() || {},
          sorts: myFilterData.getIn(["data", "sorts"])?.toJS() || [],
          groupBy: myFilterData.getIn(["data", "groupBy"]) || "bucketId",
          groupByVisibleFieldIds:
            myFilterData.getIn(["data", "groupByVisibleFieldIds"])?.toJS() || [],
          boardCardFields: myFilterData.getIn(["data", "boardCardFields"])?.toJS() || [],
          gridFields: myFilterData.getIn(["data", "gridFields"])?.toJS() || [],
          showCardLabels: myFilterData.getIn(["data", "showCardLabels"]),
        });
      }
    }
    return !isCompareImmutable(currentData, compareData);
  }, [
    projectViewId,
    myFiltersByType,
    defaultColumns,
    filters,
    sorts,
    groupBy,
    groupByVisibleFieldIds,
    boardCardFields,
    gridFields,
    showCardLabels,
  ]);

  const handleSaveView = () => {
    if (!projectViewId) return;
    const myFilterData = myFiltersByType.find((item) => item.get("id") === projectViewId);
    if (myFilterData && myFilterData.get("id")) {
      const requestData = {
        ...myFilterData.toJS(),
        data: {
          ...myFilterData.get("data").toJS(),
          filters: filters.toJS(),
          sorts: sorts.toJS(),
          groupBy,
          groupByVisibleFieldIds: groupByVisibleFieldIds.toJS(),
          boardCardFields: boardCardFields.toJS(),
          gridFields: gridFields.toJS(),
          showCardLabels,
        },
      };
      dispatch(FilterAction.saveFilter(requestData)).then(() => {
        dispatch(FilterAction.getMyFilters(projectId, contentType));
        dispatch(
          LayoutAction.setNotification({
            title: t("View Settings"),
            type: "success",
            message: t("Settings updated!"),
          })
        );
      });
    }
  };

  const sumEstimatedRevenue = () => {
    let sumEstimatedRevenue = 0.0;
    items.forEach((deal) => {
      const dealValue = parseFloat(deal.getIn(["data", "estimatedRevenue", "value"]));
      if (dealValue) {
        sumEstimatedRevenue += dealValue;
      }
    });
    return sumEstimatedRevenue;
  };

  const renderSaveView = () => {
    if (isHideViewMode) return null;
    if (!isMyFilterChange) return null;
    return (
      <div className="flex items-center gap-2">
        <button
          className="px-2 py-1 rounded-lg hover:bg-gray-100 text-xs text-gray-600 cursor-pointer truncate"
          onClick={() => {
            let resetData = {
              filters: {},
              sorts: [],
              groupBy: "bucketId",
              groupByVisibleFieldIds: [""],
              boardCardFields: [],
              gridFields: defaultColumns.toJS(),
              showCardLabels: false,
            };
            if (projectViewId) {
              const myFilterData = myFiltersByType.find((item) => item.get("id") === projectViewId);
              if (myFilterData && myFilterData.get("id")) {
                resetData = {
                  filters: myFilterData.getIn(["data", "filters"])?.toJS() || {},
                  sorts: myFilterData.getIn(["data", "sorts"])?.toJS() || [],
                  groupBy: myFilterData.getIn(["data", "groupBy"]) || "bucketId",
                  groupByVisibleFieldIds:
                    myFilterData.getIn(["data", "groupByVisibleFieldIds"])?.toJS() || [],
                  boardCardFields: myFilterData.getIn(["data", "boardCardFields"])?.toJS() || [],
                  gridFields: myFilterData.getIn(["data", "gridFields"])?.toJS() || [],
                  showCardLabels: myFilterData.getIn(["data", "showCardLabels"]),
                };
              }
            }
            onChangeFilterAndSort(resetData.filters, resetData.sorts);
            dispatch(TaskAction.changeGroupBy(resetData.groupBy, resetData.groupByVisibleFieldIds));
            dispatch(TaskAction.changeBoardCardFields(resetData.boardCardFields));
            dispatch(TaskAction.changeGridFields(resetData.gridFields));
            dispatch(TaskAction.changeShowCardLabels(resetData.showCardLabels));
          }}
        >
          {t("Discard changes")}
        </button>
        {projectViewId ? (
          <Menu as="div" className="relative inline-flex z-20">
            <button
              className={`flex items-center gap-2 pl-3 rounded-lg bg-blue-600 text-xs text-white hover:bg-blue-700 border border-gray-200 disabled:bg-gray-50 disabled:text-gray-400 cursor-pointer truncate`}
              onClick={handleSaveView}
            >
              {t("Save changes")}
              <div className="flex items-center">
                <span className="h-3 border-1 border-white/40"></span>
                <MenuButton
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  as="span"
                  className="inline-flex items-center pl-2 pr-3 py-1"
                >
                  <ChevronDown className="w-4 h-4" />
                </MenuButton>
              </div>
            </button>
            <MenuItems
              transition
              anchor="bottom end"
              className="z-50 rounded-lg border border-gray-200 bg-white p-1 transition duration-100 ease-out [--anchor-gap:0px] focus:outline-none data-closed:scale-95 data-closed:opacity-0"
            >
              <MenuItem>
                <button
                  onClick={() => {
                    dispatch(
                      TaskAction.showViewModeDialog({
                        name: "",
                        visibility: "Private",
                        data: {},
                      })
                    );
                  }}
                  className="w-full flex items-center gap-2 rounded-lg px-3 py-1 text-xs text-gray-800 hover:bg-gray-100 whitespace-nowrap data-focus:bg-gray-200 cursor-pointer"
                >
                  <Grid2X2Plus className="w-3 h-3" />
                  <div>{t("Save as new view")}</div>
                </button>
              </MenuItem>
            </MenuItems>
          </Menu>
        ) : (
          <button
            onClick={() => {
              dispatch(
                TaskAction.showViewModeDialog({
                  name: "",
                  visibility: "Private",
                  data: {},
                })
              );
            }}
            className="flex items-center gap-2 px-2 py-1 rounded-lg bg-blue-600 text-xs text-white hover:bg-blue-700 border border-gray-200 disabled:bg-gray-50 disabled:text-gray-400 cursor-pointer truncate"
          >
            <Grid2X2Plus className="w-3 h-3" />
            <div>{t("Save as new view")}</div>
          </button>
        )}
      </div>
    );
  };

  const renderTotalDealValue = () => {
    if (contentType == "SALE") {
      const value = sumEstimatedRevenue();
      const formatString = formatCompactAmount(i18n.language, value, currency);
      const noValue = t("No amount");
      if (formatString != "") {
        return (
          <div className="flex items-center px-2 py-1 rounded-full bg-gray-100 gap-1">
            <SquareSigma className="w-4 h-4 text-gray-700" />
            <div>{t("Total")}:</div>
            <div className="font-semibold">
              {formatCompactAmount(i18n.language, value, currency, noValue)}
            </div>
          </div>
        );
      }
    }
  };

  const renderItemStatistic = () => {
    return renderTotalDealValue();
  };

  const renderPagination = () => {
    return (
      <Pagination
        onChange={(page, pageSize) => {
          goToPage({ page, pageSize });
        }}
        pagination={pagination}
      />
    );
  };

  const renderRightToolbar = () => {
    if (myFilterLoading) {
      return null;
    }
    return (
      <div className={`flex justify-end space-x-4 items-center pr-6 w-full`}>
        {renderSaveView()}
        {renderItemStatistic()}
        {renderPagination()}
        {contentType === ContentTypes.ACTIVITY && appView === "Tasks" && (
          <button
            onClick={() => {
              dispatch(
                ActivityAction.showActivityDialog(null, null, {
                  activityType: ActivityTypes.task,
                  summary: "",
                })
              );
            }}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors shrink-0"
          >
            <Plus className="w-3.5 h-3.5" />
            {t("New task")}
          </button>
        )}
        {contentType === ContentTypes.ACTIVITY && appView === "Notes" && (
          <button
            onClick={() => {
              dispatch(
                ActivityAction.showActivityDialog(null, null, {
                  activityType: ActivityTypes.commented,
                  summary: "Note",
                })
              );
            }}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors shrink-0"
          >
            <Plus className="w-3.5 h-3.5" />
            {t("New note")}
          </button>
        )}
        {contentType === ContentTypes.ACTIVITY && appView === "Drafts" && (
          <button
            onClick={() => {
              dispatch(ActivityAction.showEmailDialog(projectId, null));
            }}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors shrink-0"
          >
            <Mail className="w-3.5 h-3.5" />
            {t("Compose email")}
          </button>
        )}
        <SecondToolbarAction
          onExport={onExportCSV}
          projectId={projectId}
          importType={contentType === "SALE" ? "deal" : "contact_account"}
        />
      </div>
    );
  };

  return (
    <div className={"w-full pl-1.5 py-2 flex items-center space-x-2 h-10 bg-white"}>
      <div className="flex flex-shrink-0 space-x-2">
        {!isHideViewMode && (
          <>
            <ViewMode
              projectId={projectId}
              contentType={contentType}
              projectViewId={projectViewId}
              projectView={projectView}
              appView={appView}
            />
            <div className="w-0 h-8 ml-2 mr-4 border-r-1 border-gray-300" />
            <ViewSettings
              contentType={contentType}
              projectView={projectView}
              sorts={sorts}
              onChangeSort={(sorts) => {
                onChangeFilterAndSort(filters.toJS(), sorts);
              }}
            />
          </>
        )}
        <SortBy
          contentType={contentType}
          appView={appView}
          sorts={sorts}
          onChangeSort={(sorts) => {
            onChangeFilterAndSort(filters.toJS(), sorts);
          }}
        />
        <AdvancedFilter
          contentType={contentType}
          appView={appView}
          filters={filters}
          onChangeFilter={(filters) => {
            onChangeFilterAndSort(filters, sorts.toJS());
          }}
        />
      </div>
      {renderRightToolbar()}
      <ViewModeDialog />
    </div>
  );
};

export default SearchFilter;
